<script setup>
import { ref, inject, toRefs } from 'vue'
import { formatCurrency } from '@/helpers/text'
import MembershipsAddons from './MembershipsAddons.vue'
import { CheckIcon } from '@heroicons/vue/24/solid'
import { useCartStore } from '@/stores/CartStore'
import { useRoute, useRouter } from 'vue-router'

const siteConfig = inject('siteConfig')
const route = useRoute()
const router = useRouter()


const props = defineProps({
  membership: { type: Object, required: true }
})

const { membership } = toRefs(props)
const selectedAddon = ref(null)
const autoRenew = ref(props.membership.autoRenew || siteConfig.features.defaultAutoRenewMemberships || false)

const cartStore = useCartStore()

const addMembershipToCart = (membership) => {
  // Temporarily only allow one membership in cart per Toms request
  cartStore.membershipsInCart?.forEach((item) => {
      cartStore.removeItem(item.cartId)
  })
  cartStore.addMembership(membership, selectedAddon.value, autoRenew.value)
  if (route.query && route.query?.backTo) {
    router.push({ path: route.query.backTo })
  }
  else {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      cartStore.toggleCart(true)
    }, 50)
  }
}

const handleAddons = (addon) => {
  selectedAddon.value = addon
}

const calculatePrice = () => {
  return formatCurrency(membership.value.price + selectedAddon.value.price)
}
</script>
<template>
  <template v-if="membership">
    <header class="xlarge-text xl:heading-3">
      <h2 class="flex-1">{{ membership.title }}</h2>

      <strong v-if="membership.price !== undefined"> &dollar;{{ formatCurrency(membership.price) }} </strong>
      <strong v-if="selectedAddon" class="!hidden pl-10 md:!inline-block">Total with add-on: &dollar;{{ calculatePrice() }}</strong>
      <button
        v-if="!membership.hideAddToCart"
        class="btn-cart btn-accent-1 large-bold hidden md:inline-block"
        @click="
          addMembershipToCart(
            membership
          )
        "
      >
        Add To Cart
      </button>
    </header>

    <div class="content">
      <img :src="membership.image" />
      <div class="benefits" >
        <div v-if="membership.fetched"  class="formatted-html" v-html="membership.details">

        </div>
        <div v-else>
          <p v-html="membership.benefitsIntro"></p>

          <ul :class="{ 'mb-5 xl:mb-0': membership.hasGalleryNote }">
            <li v-for="benefit in membership.benefits" :key="benefit">
              <CheckIcon class="icon" />
              <span v-html="benefit"></span>
            </li>
          </ul>

          <p
            v-if="membership.extraBenefitsTitle"
            v-html="membership.extraBenefitsTitle"
          ></p>

          <ul
            v-if="membership.extraBenefits"
            :class="{ 'mb-5 xl:mb-0': membership.hasGalleryNote }"
          >
            <li v-for="benefit in membership.extraBenefits" :key="benefit">
              <CheckIcon class="icon" />
              <span v-html="benefit"></span>
            </li>
          </ul>
        </div>
        <MembershipsAddons
          v-if="
            siteConfig.membershipsWithAddons &&
              siteConfig.membershipsWithAddons.length &&
              siteConfig.membershipsWithAddons.includes(membership.id)
          "
          @handleAddons="handleAddons"
          :selectedAddon="selectedAddon"
          :calculatePrice="calculatePrice"
        />

        <button
          v-if="!membership.hideAddToCart"
          class="btn-cart btn-accent-1 large-bold md:hidden"
          @click="
            addMembershipToCart(
              membership
            )
          "
          v-html="'Add To Cart'"
        ></button>

        <div class="flex items-right mt-5 w-full" v-if="!membership.hideAutoRenew && !siteConfig.features?.membershipsHideAutoRenew">
          <input
            type="checkbox"
            id="autoRenew"
            name="autoRenew"
            v-model="autoRenew"
            :disabled="membership.autoRenew"
            class="form-checkbox h-5 w-5 text-accent-1 self-center transition duration-150 ease-in-out"
          />
          <label for="autoRenew" class="ml-2 block leading-5 text-gray-700">
            Auto Renew
          </label>
        </div>
      </div>
    </div>
  </template>
</template>

<style lang="postcss" scoped>

 .btn-cart { @apply py-1 w-full md:ml-3 md:w-40 flex-none }
  header {
    @apply flex items-center bg-gray-100 px-3 xl:px-4 py-2.5 mb-5;
    strong {
      @apply text-accent-2 inline-block flex-none;
    }
  }
 .content {
      @apply md:flex gap-x-5;
      img {
        @apply w-full md:w-60 xl:w-64 md:h-[180px] object-cover flex-none
        mb-4 md:mb-0 aspect-[4/3];
      }

      .benefits {
        @apply relative flex-1;
        ul {
          @apply mt-2.5;
          li {
            @apply mb-2.5 flex items-center gap-x-2 xl:w-2/3;
            .icon {
              @apply w-4 h-4 text-accent-1 flex-none;
            }
          }
        }

        /* .gallery-note {
          @apply xl:mt-auto;
          .link {
            @apply font-bold text-accent-1 transition hover:text-accent-2;
          }
        } */
      }
    }
</style>
